/**
 * Reports Page Component
 *
 * Main page for the Reports module
 * Displays reports functionality in a standalone page
 */

import React from 'react';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {MarketerSelector} from '../components/MarketerSelector';
import {ReportFiltersComponent} from '../components/ReportFilters';
import {ReportTabs} from '../components/ReportTabs';
import {useReports} from '../hooks/useReports';
import {PageLoadingState} from '../../../Presentation/components/loading/PageLoadingState';
import {AppTitle, AppText, AppCardTitle} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';

const MarketerReportsPage: React.FC = () => {
  const {
    marketers,
    selectedMarketer,
    isLoadingMarketers,
    getMarketers,
    setSelectedMarketer,
    getMarketerStoresReport,
    getMarketerFinancialReport,
    getMarketerStockReport,
    filters
  } = useReports();

  React.useEffect(() => {
    getMarketers().catch(() => {
      // Error handling is done in the hook, just catch to prevent unhandled promise rejection
    });
  }, [getMarketers]);

  if (isLoadingMarketers) {
    return (
      <SidebarLayout>
        <div className="h-full flex items-center justify-center">
          <PageLoadingState message="جاري تحميل قائمة المسوقين..." />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="h-full flex flex-col p-6">
        {/* Page Header */}
        <div className="mb-6">
          <AppTitle className="text-3xl font-bold text-gray-900 mb-2">
            التقارير
          </AppTitle>
          <AppText className="text-gray-600">
            إدارة وعرض تقارير المسوقين والمبيعات
          </AppText>
        </div>

        {/* Reports Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Marketer Selection */}
            <div className="lg:col-span-1">
              <Card className="p-6 h-fit">
                <MarketerSelector
                  marketers={marketers}
                  selectedMarketer={selectedMarketer}
                  onMarketerSelect={(marketerId) => {
                    const marketer = marketers.find(m => m.id === marketerId);
                    setSelectedMarketer(marketer || null);
                    if (marketer) {
                      // Prefetch all reports so all tabs are ready
                      // Error handling is done in the hooks, just catch to prevent unhandled promise rejection
                      getMarketerStoresReport(marketer.id, filters).catch(() => {
                        // Error already handled in hook
                      });
                      getMarketerFinancialReport(marketer.id, filters).catch(() => {
                        // Error already handled in hook
                      });
                      getMarketerStockReport(marketer.id, {...filters, only_with_stock: true}).catch(() => {
                        // Error already handled in hook
                      });
                    }
                  }}
                  loading={isLoadingMarketers}
                />
              </Card>
            </div>

            {/* Reports Content */}
            <div className="lg:col-span-2 space-y-6">
              {selectedMarketer ? (
                <>
                  {/* Filters */}
                  <Card className="p-6">
                    <ReportFiltersComponent />
                  </Card>

                  {/* Report Tabs */}
                  <ReportTabs marketer={selectedMarketer} />
                </>
              ) : (
                <Card className="p-12 text-center">
                  <div className="text-gray-400 mb-4">
                    <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <AppCardTitle className="text-gray-500 mb-2">
                    اختر مسوق لعرض التقارير
                  </AppCardTitle>
                  <AppText className="text-gray-400">
                    يرجى اختيار مسوق من القائمة لعرض تقاريره المالية والمبيعات
                  </AppText>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default MarketerReportsPage;
